import { apiRequest } from "./client";
import { UserOut } from "../types/user.types";
export type UserRole = "customer" | "admin";

export const getMeRequest = async (): Promise<UserOut> => {
    return apiRequest<UserOut>("/users/me", {
        method: "GET",
        auth: true,
    });
};


export interface UserCityCountry {
    id: string;
    name_ru: string;
    name_en: string;
    name_de: string;
    created_at: string;
}

export interface UserCity {
    id: string;
    name_ru: string;
    name_en: string;
    name_de: string;
    country_id: string;
    country: UserCityCountry;
    created_at: string;
}

export interface CustomerProfile {
    id: string;
    city_id: string;
    city: UserCity | null;
    address: string | null;
    zip_code: string | null;
    phone: string | null;
    registered_at: string;
    last_auth_at: string | null;
    club_member: boolean;
    status: "active" | "blocked" | string;
    is_agent: boolean;
    sort: number;
    is_active: boolean;
    created_at: string;
    personal_discount_percent: number | null;
    promo_code_allowed: boolean;
    // Распространитель, которому выдаём билеты вручную (4-й тип в статистике).
    is_manual_distributor?: boolean;
}

export interface UserItem {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    role: UserRole;
    is_active: boolean;
    created_at: string;
    last_login_at: string | null;
    customer_profile: CustomerProfile | null;
}

export interface UsersListResponse {
    items: UserItem[];
    page: number;
    page_size: number;
    total_size: number;
}

export interface UsersListParams {
    page?: number;
    page_size?: number;
    sort_by?: "first_name" | "email" | "created_at";
    role?: UserRole;
    is_agent?: boolean;
    club_member?: boolean;
    is_active?: boolean;
    first_name?: string;
    email?: string;
}

export interface UpdateUserPayload {
    is_active?: boolean;
    is_agent?: boolean;
    club_member?: boolean;
    personal_discount_percent?: number | null;
    promo_code_allowed?: boolean;
    is_manual_distributor?: boolean;
    first_name?: string;
    last_name?: string;
    address?: string;
    zip_code?: string;
    phone?: string;
    city_id?: string;
}

const buildQueryString = (params: UsersListParams): string => {
    const searchParams = new URLSearchParams();

    if (params.page) searchParams.set("page", String(params.page));
    if (params.page_size) searchParams.set("page_size", String(params.page_size));
    if (params.sort_by) searchParams.set("sort_by", params.sort_by);
    if (params.role) searchParams.set("role", params.role);
    if (typeof params.is_agent === "boolean") {
        searchParams.set("is_agent", String(params.is_agent));
    }
    if (typeof params.club_member === "boolean") {
        searchParams.set("club_member", String(params.club_member));
    }
    if (typeof params.is_active === "boolean") {
        searchParams.set("is_active", String(params.is_active));
    }
    if (params.first_name) searchParams.set("first_name", params.first_name);
    if (params.email) searchParams.set("email", params.email);

    const query = searchParams.toString();

    return query ? `?${query}` : "";
};

export const getUsersRequest = async (
    params: UsersListParams,
): Promise<UsersListResponse> => {
    const query = buildQueryString(params);

    return apiRequest<UsersListResponse>(`/users/${query}`, {
        method: "GET",
        auth: true,
    });
};

export const updateUserRequest = async (
    userId: string,
    payload: UpdateUserPayload,
): Promise<UserItem> => {
    return apiRequest<UserItem>(`/users/${userId}`, {
        method: "PATCH",
        auth: true,
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
        },
    });
};

// ---------- Отправка письма распространителю из админки ----------

export interface SendDistributorMessagePayload {
    subject: string;
    body: string;
    attachment?: File | null;
}

export const sendDistributorMessageRequest = async (
    userId: string,
    payload: SendDistributorMessagePayload,
): Promise<{ status: string }> => {
    const formData = new FormData();
    formData.append("subject", payload.subject);
    formData.append("body", payload.body);

    if (payload.attachment) {
        formData.append("attachment", payload.attachment);
    }

    // Content-Type не указываем: браузер сам проставит multipart/form-data с boundary.
    return apiRequest<{ status: string }>(`/users/${userId}/send-distributor-message`, {
        method: "POST",
        auth: true,
        body: formData,
    });
};

// ---------- Экспорт распространителей в Excel ----------

export const downloadAgentsExportRequest = async (): Promise<{
    blob: Blob;
    filename: string;
}> => {
    const { API_BASE_URL } = await import("../config/env");
    const { getAccessToken } = await import("../storage/token.storage");

    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}/export/agents.xlsx`, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try {
            const data = await response.json();
            if (data?.detail) detail = String(data.detail);
        } catch {
            // ignore
        }
        throw new Error(detail);
    }

    const blob = await response.blob();

    let filename = "agents.xlsx";
    const cd = response.headers.get("content-disposition");
    if (cd) {
        const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
        if (m && m[1]) filename = decodeURIComponent(m[1]);
    }

    return { blob, filename };
};
